import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../user-feedback/tooltip";
import IconProvider from "./IconProvider";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-purple-1 text-white hover:bg-purple-2",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-purple-1 bg-background hover:bg-purple-1 hover:text-white",
        secondary: "bg-purple-5 text-purple-1 hover:bg-purple-4",
        ghost: "hover:bg-purple-4 hover:text-purple-1",
        halfghost:
          "hover:bg-purple-4 bg-muted hover:text-purple-1 text-muted-foreground",
        link: "text-purple-1 underline-offset-4 hover:underline",
        selection:
          "bg-muted text-muted-foreground hover:bg-muted-foreground/20 data-[state=active]:bg-purple-4 data-[state=active]:text-purple-1",
        toggleCheck:
          "border border-purple-1 bg-background hover:bg-purple-1 hover:text-white transition-all duration-200",
        icon: " hover:text-purple-1 hover:bg-muted",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded px-3",
        lg: "h-11 rounded px-8",
        icon: "h-10 w-10",
        iconButton: "h-8 w-8",
        iconSmall: "h-6 w-6",
        badge: "h-4 w-4 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  tooltip?: string;
  isChecked?: boolean;
  onToggle?: (checked: boolean) => void;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      tooltip,
      isChecked = false,
      onToggle,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (variant === "toggleCheck" && onToggle) {
        onToggle(!isChecked);
      }
      if (onClick) {
        onClick(e);
      }
    };

    // For toggleCheck variant, modify the styling based on checked state
    const getToggleCheckClass = () => {
      if (variant !== "toggleCheck") return "";
      return isChecked
        ? "bg-purple-1 text-white border-purple-1"
        : "border-purple-1 bg-background text-purple-1";
    };

    const buttonElement = (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          variant === "toggleCheck" ? getToggleCheckClass() : "",
          className
        )}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        {children}
        {variant === "toggleCheck" && isChecked && (
          <IconProvider iconstring="fa-check" className="w-4 h-4" />
        )}
      </Comp>
    );

    if (tooltip) {
      return (
        <TooltipProvider delayDuration={100} skipDelayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
            <TooltipContent>
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return buttonElement;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
